import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { Star } from "lucide-react";

import { submitMeetupSurvey } from "@/lib/api/participants";
import { getParticipantSession } from "@/lib/participant-session";

import { Button, LinkButton } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Field } from "@/components/ui/Field";
import { Textarea } from "@/components/ui/Input";
import { Logo } from "@/components/ui/Logo";
import { MobileFrame } from "@/components/ui/MobileFrame";
import { GlobalFooter } from "@/components/layout/GlobalFooter";
import { cn } from "@/lib/utils";

export function SurveyPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const session = slug ? getParticipantSession(slug) : null;

  useEffect(() => {
    if (!slug) return;
    if (!session) navigate(`/e/${slug}`, { replace: true });
  }, [slug, session, navigate]);

  const [relevance, setRelevance] = useState<number | null>(null);
  const [metSomeone, setMetSomeone] = useState<boolean | null>(null);
  const [nps, setNps] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const mutation = useMutation({
    mutationFn: () =>
      submitMeetupSurvey({
        participantId: session?.participantId ?? "",
        relevance: relevance ?? 0,
        metSomeone: metSomeone ?? false,
        nps: nps ?? 0,
        comment: comment.trim() || undefined,
      }),
    onSuccess: () => setSubmitted(true),
  });

  if (!session) return null;

  if (submitted) {
    return (
      <MobileFrame>
        <div className="flex-1 px-4 py-6">
          <Card padding="lg" className="text-center animate-fade-in">
            <div className="text-5xl">🙏</div>
            <h2 className="mt-3 text-[22px] font-bold tracking-tight text-[#1a1a1a]">
              Thank you
            </h2>
            <p className="mt-1 text-[13px] text-[#6c757d]">
              Your feedback helps us make Finally better for the next event.
            </p>
            <LinkButton to={`/e/${slug}/matches`} className="mt-6">
              Back to Matches
            </LinkButton>
          </Card>
        </div>
      </MobileFrame>
    );
  }

  const canSubmit =
    relevance !== null && metSomeone !== null && nps !== null && !mutation.isPending;

  return (
    <MobileFrame>
      <div className="flex-1 px-4 py-6 animate-fade-in">
        <h1 className="text-[24px] font-bold tracking-tight text-[#1a1a1a]">How was the event?</h1>
        <p className="mt-1 text-[13px] text-[#6c757d]">
          Four quick answers. Optional comment at the end.
        </p>

        <Card padding="md" className="mt-5">
          <p className="text-[14px] font-bold text-[#2563eb]">
            How relevant were your recommendations?
          </p>
          <div className="mt-3 flex gap-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setRelevance(n)}
                aria-label={`${n} stars`}
                className={cn(
                  "grid h-12 w-12 place-items-center rounded-[10px] border transition-colors",
                  relevance !== null && n <= relevance
                    ? "border-[#f59e0b] bg-[#fffbeb] text-[#f59e0b]"
                    : "border-[#e2e8f0] bg-white text-[#94a3b8] hover:bg-[#fffbeb]",
                )}
              >
                <Star
                  className="h-5 w-5"
                  fill={relevance !== null && n <= relevance ? "currentColor" : "none"}
                />
              </button>
            ))}
          </div>
        </Card>

        <Card padding="md" className="mt-4">
          <p className="text-[14px] font-bold text-[#2563eb]">
            Did you meet someone from your matches?
          </p>
          <div className="mt-3 flex gap-2">
            {[
              { value: true, label: "Yes" },
              { value: false, label: "No" },
            ].map((opt) => (
              <button
                key={String(opt.value)}
                type="button"
                onClick={() => setMetSomeone(opt.value)}
                className={cn(
                  "h-12 flex-1 rounded-[10px] border text-[14px] font-semibold transition-colors",
                  metSomeone === opt.value
                    ? "border-[#2563eb] bg-[#2563eb] text-white"
                    : "border-[#e2e8f0] bg-white text-[#1a1a1a] hover:border-[#cbd5e1]",
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </Card>

        <Card padding="md" className="mt-4">
          <p className="text-[14px] font-bold text-[#2563eb]">
            Would you recommend Finally to a friend?
          </p>
          <p className="mt-1 text-[12px] text-[#94a3b8]">0 — not likely · 10 — very likely</p>
          <div className="mt-3 grid grid-cols-11 gap-1">
            {Array.from({ length: 11 }, (_, i) => i).map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setNps(n)}
                className={cn(
                  "rounded-[8px] border py-2 text-[13px] font-semibold transition-colors",
                  nps === n
                    ? "border-[#2563eb] bg-[#2563eb] text-white"
                    : "border-[#e2e8f0] bg-white text-[#1a1a1a] hover:bg-[#f8fafc]",
                )}
              >
                {n}
              </button>
            ))}
          </div>
        </Card>

        <Card padding="md" className="mt-4">
          <Field label="Any feedback or suggestions?">
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={4}
              placeholder="Optional"
            />
          </Field>
        </Card>

        <Button
          onClick={() => mutation.mutate()}
          disabled={!canSubmit}
          loading={mutation.isPending}
          fullWidth
          className="mt-5"
        >
          {mutation.isPending ? "Submitting..." : "Submit Feedback"}
        </Button>
        {!canSubmit && !mutation.isPending ? (
          <p className="mt-2 text-center text-[12px] text-[#94a3b8]">
            {[
              relevance === null && "rate relevance",
              metSomeone === null && "answer Yes / No",
              nps === null && "pick an NPS score",
            ]
              .filter(Boolean)
              .join(" · ") + " to enable submit"}
          </p>
        ) : null}
        <Link
          to={`/e/${slug}/matches`}
          className="mt-3 block text-center text-[13px] text-[#6c757d] hover:underline"
        >
          Skip
        </Link>
      </div>

    </MobileFrame>
  );
}
